'use client'

import { useCartController } from 'cart/hooks/useCartController'
import { ButtonPrimary } from 'fukku/Button/Primary'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import type { ProductLocations } from 'product/types/ProductLocations'
import { useState } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import { useSizes } from '../../contexts/SizesProvider/SizesProvider'
import type { CommonProps } from '../../types/CommonProps'
import { getIsBackInStock } from '../../utils/getIsBackInStock/getIsBackInStock'
import { canAddProduct } from './utils/canAddProduct'

import styles from './AddToBagButton.module.scss'

export type AddToBagProps = Pick<CommonProps, 'product' | 'look'> & {
	location: ProductLocations
	dataTestId?: string
	className?: string
}

export function AddToBagButton({
	product,
	look,
	dataTestId,
	location,
	className,
}: AddToBagProps): React.ReactNode {
	const { t } = useLabels()
	const { isLessThanLarge, isLargeOrGreater } = useResponsive()
	const { selectedSize, openSheet, hasRelatedSizes } = useSizes()
	const { selectedColor } = useColors()
	const { add } = useCartController()
	const [errorMessage, setErrorMessage] = useState<boolean>(false)
	const {
		country: { hasOnlineSale },
	} = useMasterData()

	const backInStockBtn = getIsBackInStock(
		hasRelatedSizes,
		hasOnlineSale,
		selectedSize,
		selectedColor
	)

	const handleToggleErrorMessage = (show: boolean): void => {
		if (!selectedSize && isLargeOrGreater) {
			setErrorMessage(show)
		}
	}

	const handleAddToCart = (): void => {
		const canAddToBag = canAddProduct(
			selectedSize,
			isLargeOrGreater,
			selectedColor,
			hasRelatedSizes
		)

		if (canAddToBag) {
			add(
				{
					productId: product.id,
					colorId: selectedColor.id,
					sizeId: selectedSize?.id || '',
					seller: product.seller,
					lookId: look.id,
					lastUnits: !!selectedSize?.lastUnits,
				},
				location
			)
			return
		}

		if (isLessThanLarge) {
			openSheet(true)
			return
		}

		handleToggleErrorMessage(true)
	}

	if (backInStockBtn || !selectedColor.isSellable) {
		return null
	}

	return (
		<ButtonPrimary
			className={cx(errorMessage && styles.btnErrorMessage, className)}
			onClick={handleAddToCart}
			data-testid={dataTestId}
			onMouseEnter={() => handleToggleErrorMessage(true)}
			onMouseLeave={() => handleToggleErrorMessage(false)}
			onFocus={() => handleToggleErrorMessage(true)}
			onBlur={() => handleToggleErrorMessage(false)}
		>
			{errorMessage ? t('pdp.selectSize.button') : t('pdp.add.button')}
		</ButtonPrimary>
	)
}
